import type { ConfigType, Dayjs } from 'dayjs'
import type {
  Location,
  LocationHours,
  LocationId,
  LocationFulfillmentWindow,
} from '@/lib/services/locations/locations.types'
import type { AvailabilityTemporaryHoursWindow } from '@/lib/services/availability-changes/availability-changes.types'
import type {
  AvailabilityChangeFields,
  AvailabilityChangeOccurrence,
} from '@/lib/services/availability-changes/availability-changes.utils'
import type { FlowerSubscriptionScheduleOptions } from '@/lib/services/flower-subscriptions/flower-subscriptions.types'
import type { FulfillmentLocationLike } from '@/lib/utils'

type FulfillmentHours = Pick<LocationHours, 'id' | 'is_open' | 'open' | 'close'>
type FulfillmentWindow = Pick<LocationFulfillmentWindow, 'date' | 'can_pickup' | 'can_deliver'>

/** Both a hydrated location and the partial location references used before its query completes. */
export type PosFulfillmentLocation = Omit<
  NonNullable<FulfillmentLocationLike>,
  'fulfillment_windows'
> &
  Partial<Pick<Location, 'is_pickup' | 'is_delivery'>> & {
    id?: LocationId | null
    fulfillment_windows?: FulfillmentWindow[] | null
    open_hours?: FulfillmentHours[] | null
    delivery_hours?: FulfillmentHours[] | null
    availability_changes?: AvailabilityChangeFields[] | null
  }
type LocationInput = PosFulfillmentLocation | null | undefined
type HoursDay = FulfillmentHours | AvailabilityTemporaryHoursWindow
type DateKey = string | null
type AvailabilityWindow = [Dayjs, Dayjs]
interface AvailabilityOptions {
  isPickup?: boolean
  now?: ConfigType
  useHoursFallbackForMissingWindows?: boolean
}
export interface PosFulfillmentAvailability {
  min: string
  max: DateKey
  tz: string
  hardDisabledDates: Set<DateKey>
  softBlockedDates: Set<DateKey>
  softBlockedDateReasons: Map<DateKey, string>
  isFallback: boolean
}
type AvailabilityInput = Partial<PosFulfillmentAvailability> | null | undefined
type ScheduleOptions =
  | Partial<
      Pick<
        FlowerSubscriptionScheduleOptions,
        | 'earliest_fulfillment_date'
        | 'latest_fulfillment_date'
        | 'available_fulfillment_dates'
        | 'unavailable_fulfillment_dates'
      >
    >
  | null
  | undefined
interface AvailabilityWarning {
  title: string
  description: string
}
type HydratedLocation = PosFulfillmentLocation & {
  address: { timezone: string }
  fulfillment_windows: NonNullable<PosFulfillmentLocation['fulfillment_windows']>
  availability_changes: AvailabilityChangeFields[]
}

import dayjs from '@/lib/dayjs'
import { getAvailabilityChangeOccurrencesInLocalDateRange } from '@/lib/services/availability-changes/availability-changes.utils'
import { calculateDisabledDates } from '@/lib/utils'

const POS_FULFILLMENT_MAX_DAYS = 365
const STORE_CLOSED_REASON = 'store_closed'
const NO_PICKUP_REASON = 'pickup_paused'
const NO_DELIVERY_REASON = 'delivery_paused'
const UNAVAILABLE_REASON = 'unavailable'
const FULFILLMENT_DISABLED_REASON = 'fulfillment_disabled'
const AVAILABILITY_NOT_GENERATED_REASON = 'availability_not_generated'
const OUTSIDE_HOURS_OR_AVAILABILITY_REASON = 'outside_hours_or_availability'

function getRelevantHours(
  location: LocationInput,
  isPickup: boolean
): FulfillmentHours[] | null | undefined {
  return isPickup ? location?.open_hours : location?.delivery_hours
}

function getEnabledField(location: LocationInput, isPickup: boolean): boolean | undefined {
  return isPickup ? location?.is_pickup : location?.is_delivery
}

function getWindowEnabledField(isPickup: boolean): 'can_pickup' | 'can_deliver' {
  return isPickup ? 'can_pickup' : 'can_deliver'
}

function getAvailabilityChangeBlockField(isPickup: boolean): 'pickup_paused' | 'delivery_paused' {
  return isPickup ? 'pickup_paused' : 'delivery_paused'
}

function getFulfillmentUnavailableReason(isPickup: boolean): string {
  return isPickup ? NO_PICKUP_REASON : NO_DELIVERY_REASON
}

function toLocalDay(value: ConfigType, tz: string | undefined): Dayjs | null {
  if (!value) {
    return null
  }

  if (dayjs.isDayjs(value)) {
    return value.tz(tz)
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return dayjs.tz(value.trim(), 'YYYY-MM-DD', tz)
  }

  return dayjs(value).tz(tz)
}

function toDateYmd(value: ConfigType, tz: string | undefined): DateKey {
  const localDay = toLocalDay(value, tz)

  if (!localDay?.isValid()) {
    return null
  }

  return localDay.format('YYYY-MM-DD')
}

function addLocalDays(dateYmd: DateKey, days: number, tz: string): DateKey {
  const localDay = dayjs.tz(dateYmd, 'YYYY-MM-DD', tz)

  if (!localDay.isValid()) {
    return null
  }

  return localDay.add(days, 'day').format('YYYY-MM-DD')
}

function getNowInTimezone(tz: string, now: ConfigType): Dayjs {
  if (dayjs.isDayjs(now)) {
    return now.tz(tz)
  }

  if (typeof now === 'string' && now.trim()) {
    return dayjs.tz(now.trim(), tz)
  }

  if (now) {
    return dayjs(now).tz(tz)
  }

  return dayjs().tz(tz)
}

function getHoursForDay(
  hours: FulfillmentHours[] | null | undefined = [],
  dayOfWeek: number
): FulfillmentHours | null {
  if (!Array.isArray(hours)) {
    return null
  }

  return hours.find((entry) => Number(entry?.id) === dayOfWeek) ?? hours[dayOfWeek] ?? null
}

function parseLocalTimeOnDate(
  dateYmd: DateKey,
  time: string | null | undefined,
  tz: string
): Dayjs | null {
  if (typeof time !== 'string' || !time.trim()) {
    return null
  }

  const parsed = dayjs.tz(`${dateYmd} ${time.trim()}`, 'YYYY-MM-DD HH:mm', tz)

  return parsed.isValid() ? parsed : null
}

function buildAvailabilityWindowFromHours(
  dateYmd: DateKey,
  hours: HoursDay | null | undefined,
  tz: string,
  nowLocal: Dayjs
): AvailabilityWindow[] {
  const dayLocal = dayjs.tz(dateYmd, 'YYYY-MM-DD', tz)

  if (!hours?.is_open) {
    return []
  }

  const openLocal = parseLocalTimeOnDate(dateYmd, hours.open, tz)
  const closeLocal = parseLocalTimeOnDate(dateYmd, hours.close, tz)

  if (!openLocal || !closeLocal || !closeLocal.isAfter(openLocal)) {
    return []
  }

  const startLocal =
    dayLocal.isSame(nowLocal, 'day') && nowLocal.isAfter(openLocal) ? nowLocal : openLocal

  if (!closeLocal.isAfter(startLocal)) {
    return []
  }

  return [[startLocal, closeLocal]]
}

function buildAvailabilityWindow(
  dateYmd: DateKey,
  hoursConfig: FulfillmentHours[] | null | undefined,
  tz: string,
  nowLocal: Dayjs
): AvailabilityWindow[] {
  const dayLocal = dayjs.tz(dateYmd, 'YYYY-MM-DD', tz)
  const hours = getHoursForDay(hoursConfig, dayLocal.day())

  return buildAvailabilityWindowFromHours(dateYmd, hours, tz, nowLocal)
}

function getTemporaryHoursForFulfillment(
  rule: AvailabilityChangeFields,
  isPickup: boolean
): AvailabilityTemporaryHoursWindow | null {
  const key = isPickup ? 'pickup' : 'delivery'
  const temporaryHours = rule?.temporary_hours?.[key]

  return temporaryHours && typeof temporaryHours === 'object' ? temporaryHours : null
}

function buildAvailabilityChangeOccurrencesByDate(
  availabilityChanges: AvailabilityChangeFields[] | null | undefined,
  startDateYmd: string,
  endDateYmd: DateKey,
  tz: string
): Map<DateKey, AvailabilityChangeOccurrence[]> {
  const occurrencesByDate = new Map<DateKey, AvailabilityChangeOccurrence[]>()
  const rangeStart = dayjs.tz(startDateYmd, 'YYYY-MM-DD', tz).startOf('day')
  const rangeEnd = dayjs.tz(endDateYmd, 'YYYY-MM-DD', tz).startOf('day')

  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeStart.isAfter(rangeEnd)) {
    return occurrencesByDate
  }

  getAvailabilityChangeOccurrencesInLocalDateRange(
    availabilityChanges,
    startDateYmd,
    endDateYmd,
    tz
  ).forEach((occurrence) => {
    const occurrenceStart = dayjs(occurrence.starts_at).tz(tz).startOf('day')
    const occurrenceEnd = dayjs(occurrence.ends_at).tz(tz).subtract(1, 'millisecond').startOf('day')

    if (!occurrenceStart.isValid() || !occurrenceEnd.isValid()) {
      return
    }

    let dateYmd = (occurrenceStart.isBefore(rangeStart) ? rangeStart : occurrenceStart).format(
      'YYYY-MM-DD'
    )
    const lastDateYmd = (occurrenceEnd.isAfter(rangeEnd) ? rangeEnd : occurrenceEnd).format(
      'YYYY-MM-DD'
    )

    while (dateYmd <= lastDateYmd) {
      const occurrences = occurrencesByDate.get(dateYmd) ?? []
      const nextDateYmd = addLocalDays(dateYmd, 1, tz)

      occurrences.push(occurrence)
      occurrencesByDate.set(dateYmd, occurrences)

      if (!nextDateYmd || nextDateYmd <= dateYmd) {
        break
      }

      dateYmd = nextDateYmd
    }
  })

  return occurrencesByDate
}

function getAvailabilityChangeBlockReason(
  occurrences: AvailabilityChangeOccurrence[],
  isPickup: boolean
): string | null {
  const availabilityChangeBlockField = getAvailabilityChangeBlockField(isPickup)

  if (
    occurrences.some(
      (rule) => rule?.store_closed === true && rule?.[availabilityChangeBlockField] === true
    )
  ) {
    return STORE_CLOSED_REASON
  }

  if (occurrences.some((rule) => rule?.[availabilityChangeBlockField] === true)) {
    return getFulfillmentUnavailableReason(isPickup)
  }

  return null
}

function getTemporaryAvailabilityWindow(
  dateYmd: DateKey,
  occurrences: AvailabilityChangeOccurrence[],
  isPickup: boolean,
  tz: string,
  nowLocal: Dayjs
): AvailabilityWindow[] | null {
  const temporaryHours = [...occurrences]
    .reverse()
    .map((rule) => getTemporaryHoursForFulfillment(rule, isPickup))
    .find(Boolean)

  if (!temporaryHours) {
    return null
  }

  return buildAvailabilityWindowFromHours(dateYmd, temporaryHours, tz, nowLocal)
}

function applyAvailabilityChangeOverlap(
  windows: AvailabilityWindow[],
  occurrences: AvailabilityChangeOccurrence[],
  isPickup: boolean,
  tz: string
): AvailabilityWindow[] {
  const availabilityChangeBlockField = getAvailabilityChangeBlockField(isPickup)

  return occurrences.reduce((availableWindows, rule) => {
    if (!rule?.[availabilityChangeBlockField]) {
      return availableWindows
    }

    const ruleStart = dayjs(rule.starts_at).tz(tz)
    const ruleEnd = dayjs(rule.ends_at).tz(tz)

    if (!ruleStart.isValid() || !ruleEnd.isValid() || !ruleEnd.isAfter(ruleStart)) {
      return availableWindows
    }

    return availableWindows.flatMap(([windowStart, windowEnd]) => {
      if (!ruleEnd.isAfter(windowStart) || !windowEnd.isAfter(ruleStart)) {
        return [[windowStart, windowEnd]]
      }

      const nextWindows: AvailabilityWindow[] = []

      if (ruleStart.isAfter(windowStart)) {
        nextWindows.push([windowStart, ruleStart])
      }

      if (windowEnd.isAfter(ruleEnd)) {
        nextWindows.push([ruleEnd, windowEnd])
      }

      return nextWindows
    })
  }, windows)
}

function hasRawFulfillmentAvailabilityOnDate(
  location: LocationInput,
  dateYmd: DateKey,
  {
    isPickup = false,
    now,
    occurrences = [],
  }: AvailabilityOptions & { occurrences?: AvailabilityChangeOccurrence[] } = {}
): boolean {
  const tz = location?.address?.timezone
  const hoursConfig = getRelevantHours(location, isPickup)

  if (!tz || !getEnabledField(location, isPickup) || !Array.isArray(hoursConfig)) {
    return false
  }

  const nowLocal = getNowInTimezone(tz, now)
  const temporaryWindows = getTemporaryAvailabilityWindow(
    dateYmd,
    occurrences,
    isPickup,
    tz,
    nowLocal
  )
  const initialWindows =
    temporaryWindows ?? buildAvailabilityWindow(dateYmd, hoursConfig, tz, nowLocal)

  if (!initialWindows.length) {
    return false
  }

  const availableWindows = applyAvailabilityChangeOverlap(initialWindows, occurrences, isPickup, tz)

  return availableWindows.some(([windowStart, windowEnd]) => windowEnd.isAfter(windowStart))
}

function buildStrictFallbackAvailability(
  location: LocationInput,
  { isPickup = false, now }: AvailabilityOptions = {}
): PosFulfillmentAvailability {
  const strictAvailability = calculateDisabledDates(location, { isPickup })
  const tz = strictAvailability.tz
  const todayYmd = getNowInTimezone(tz, now).startOf('day').format('YYYY-MM-DD')
  const maxYmd = addLocalDays(todayYmd, POS_FULFILLMENT_MAX_DAYS, tz)
  const windows = location?.fulfillment_windows || []
  const windowByDate = new Map<
    DateKey,
    NonNullable<PosFulfillmentLocation['fulfillment_windows']>[number]
  >(windows.map((window) => [String(window?.date ?? ''), window]))
  const softBlockedDates = new Set<DateKey>()
  const softBlockedDateReasons = new Map<DateKey, string>()
  let dateYmd: DateKey = todayYmd

  for (let dayOffset = 0; dayOffset <= POS_FULFILLMENT_MAX_DAYS; dayOffset += 1) {
    if (strictAvailability.dates?.has(dateYmd)) {
      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(dateYmd, getFulfillmentUnavailableReason(isPickup))
      dateYmd = addLocalDays(dateYmd, 1, tz)
      continue
    }

    if (windows.length && !windowByDate.has(dateYmd)) {
      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(dateYmd, AVAILABILITY_NOT_GENERATED_REASON)
    }

    dateYmd = addLocalDays(dateYmd, 1, tz)
  }

  return {
    min: todayYmd,
    max: maxYmd,
    tz: strictAvailability.tz,
    hardDisabledDates: new Set<DateKey>(),
    softBlockedDates,
    softBlockedDateReasons,
    isFallback: true,
  }
}

function hasHydratedOverrideInputs(
  location: LocationInput,
  isPickup: boolean
): location is HydratedLocation {
  return Boolean(
    location?.address?.timezone &&
    Array.isArray(location?.fulfillment_windows) &&
    Array.isArray(location?.availability_changes) &&
    Array.isArray(getRelevantHours(location, isPickup))
  )
}

export function getPosFulfillmentDateAvailability(
  location: LocationInput,
  { isPickup = false, now, useHoursFallbackForMissingWindows = false }: AvailabilityOptions = {}
): PosFulfillmentAvailability {
  if (!hasHydratedOverrideInputs(location, isPickup)) {
    return buildStrictFallbackAvailability(location, { isPickup, now })
  }

  const tz = location.address.timezone
  const todayYmd = getNowInTimezone(tz, now).startOf('day').format('YYYY-MM-DD')
  const maxYmd = addLocalDays(todayYmd, POS_FULFILLMENT_MAX_DAYS, tz)
  const windowEnabledField = getWindowEnabledField(isPickup)
  const windowByDate = new Map<
    DateKey,
    NonNullable<PosFulfillmentLocation['fulfillment_windows']>[number]
  >((location.fulfillment_windows || []).map((window) => [String(window?.date ?? ''), window]))
  const isFulfillmentEnabled = Boolean(getEnabledField(location, isPickup))
  const hardDisabledDates = new Set<DateKey>()
  const softBlockedDates = new Set<DateKey>()
  const softBlockedDateReasons = new Map<DateKey, string>()
  const totalDays = POS_FULFILLMENT_MAX_DAYS
  const occurrencesByDate = buildAvailabilityChangeOccurrencesByDate(
    location.availability_changes,
    todayYmd,
    maxYmd,
    tz
  )
  let dateYmd: DateKey = todayYmd

  for (let dayOffset = 0; dayOffset <= totalDays; dayOffset += 1) {
    const window = windowByDate.get(dateYmd)
    const occurrences = occurrencesByDate.get(dateYmd) ?? []
    const ruleBlockReason = getAvailabilityChangeBlockReason(occurrences, isPickup)

    if (!isFulfillmentEnabled) {
      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(dateYmd, FULFILLMENT_DISABLED_REASON)
      dateYmd = addLocalDays(dateYmd, 1, tz)
      continue
    }

    if (!window) {
      if (ruleBlockReason) {
        softBlockedDates.add(dateYmd)
        softBlockedDateReasons.set(dateYmd, ruleBlockReason)
        dateYmd = addLocalDays(dateYmd, 1, tz)
        continue
      }

      if (
        useHoursFallbackForMissingWindows &&
        hasRawFulfillmentAvailabilityOnDate(location, dateYmd, {
          isPickup,
          now,
          occurrences,
        })
      ) {
        dateYmd = addLocalDays(dateYmd, 1, tz)
        continue
      }

      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(
        dateYmd,
        useHoursFallbackForMissingWindows
          ? OUTSIDE_HOURS_OR_AVAILABILITY_REASON
          : AVAILABILITY_NOT_GENERATED_REASON
      )
      dateYmd = addLocalDays(dateYmd, 1, tz)
      continue
    }

    if (!window?.[windowEnabledField]) {
      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(
        dateYmd,
        ruleBlockReason ?? getFulfillmentUnavailableReason(isPickup)
      )
      dateYmd = addLocalDays(dateYmd, 1, tz)
      continue
    }

    if (
      !hasRawFulfillmentAvailabilityOnDate(location, dateYmd, {
        isPickup,
        now,
        occurrences,
      })
    ) {
      softBlockedDates.add(dateYmd)
      softBlockedDateReasons.set(dateYmd, ruleBlockReason ?? OUTSIDE_HOURS_OR_AVAILABILITY_REASON)
    }

    dateYmd = addLocalDays(dateYmd, 1, tz)
  }

  return {
    min: todayYmd,
    max: maxYmd,
    tz,
    hardDisabledDates,
    softBlockedDates,
    softBlockedDateReasons,
    isFallback: false,
  }
}

export function applyFulfillmentScheduleOptions(
  availability: PosFulfillmentAvailability,
  scheduleOptions: ScheduleOptions,
  { isPickup = false }: Pick<AvailabilityOptions, 'isPickup'> = {}
): PosFulfillmentAvailability {
  if (!scheduleOptions?.earliest_fulfillment_date || !scheduleOptions?.latest_fulfillment_date) {
    return availability
  }

  const softBlockedDates = new Set(availability?.softBlockedDates ?? [])
  const softBlockedDateReasons = new Map(availability?.softBlockedDateReasons ?? [])

  for (const date of scheduleOptions.available_fulfillment_dates ?? []) {
    softBlockedDates.delete(date)
    softBlockedDateReasons.delete(date)
  }

  for (const date of scheduleOptions.unavailable_fulfillment_dates ?? []) {
    softBlockedDates.add(date)
    if (
      !softBlockedDateReasons.has(date) ||
      softBlockedDateReasons.get(date) === AVAILABILITY_NOT_GENERATED_REASON
    ) {
      softBlockedDateReasons.set(date, getFulfillmentUnavailableReason(isPickup))
    }
  }

  return {
    ...availability,
    min: scheduleOptions.earliest_fulfillment_date,
    max: scheduleOptions.latest_fulfillment_date,
    hardDisabledDates: new Set(availability?.hardDisabledDates ?? []),
    softBlockedDates,
    softBlockedDateReasons,
  }
}

function isPosSoftOverrideDate(
  availability: AvailabilityInput,
  value: ConfigType
): availability is NonNullable<AvailabilityInput> & { softBlockedDates: Set<DateKey> } {
  if (!availability?.softBlockedDates) {
    return false
  }

  const dateYmd = toDateYmd(value, availability.tz)

  if (!dateYmd) {
    return false
  }

  return availability.softBlockedDates.has(dateYmd)
}

function getPosSoftOverrideDateReason(
  availability: AvailabilityInput,
  value: ConfigType
): string | null {
  if (!isPosSoftOverrideDate(availability, value)) {
    return null
  }

  const dateYmd = toDateYmd(value, availability.tz)

  return availability.softBlockedDateReasons?.get(dateYmd) ?? UNAVAILABLE_REASON
}

export function isPosFulfillmentOverrideDate(
  availability: AvailabilityInput,
  value: ConfigType
): boolean {
  return isPosSoftOverrideDate(availability, value)
}

export function isPosCutoffOverrideDate(
  availability: AvailabilityInput,
  value: ConfigType
): boolean {
  return isPosSoftOverrideDate(availability, value)
}

export function getPosFulfillmentDateBlockKind(
  availability: AvailabilityInput,
  value: ConfigType
): 'closed' | 'unavailable' | null {
  const reason = getPosSoftOverrideDateReason(availability, value)

  if (!reason) {
    return null
  }

  return reason === STORE_CLOSED_REASON ? 'closed' : 'unavailable'
}

export function getPosFulfillmentDateBlockTooltip(
  availability: AvailabilityInput,
  value: ConfigType,
  { isPickup = false }: Pick<AvailabilityOptions, 'isPickup'> = {}
): string | null {
  const reason = getPosSoftOverrideDateReason(availability, value)

  if (!reason) {
    return null
  }

  const label = isPickup ? 'Pickup' : 'Delivery'

  if (reason === STORE_CLOSED_REASON) {
    return 'Store closed on this date'
  }

  if (reason === FULFILLMENT_DISABLED_REASON) {
    return `${label} is not enabled for this location`
  }

  if (reason === AVAILABILITY_NOT_GENERATED_REASON) {
    return `${label} availability has not been generated for this date`
  }

  return `${label} unavailable on this date`
}

export function getFulfillmentDateAvailabilityWarning(
  availability: AvailabilityInput,
  value: ConfigType,
  {
    isPickup = false,
    description = null,
    title = null,
  }: Pick<AvailabilityOptions, 'isPickup'> & {
    description?: string | null
    title?: string | null
  } = {}
): AvailabilityWarning | null {
  if (!isPosSoftOverrideDate(availability, value)) {
    return null
  }

  const label = isPickup ? 'pickup' : 'delivery'

  return {
    title: title ?? `Outside normal ${label} availability`,
    description:
      description ??
      `This ${label} date is outside the location's normal ${label} availability. Review this date before saving if the order can still be fulfilled.`,
  }
}

export function getPosFulfillmentDateWarning(
  availability: AvailabilityInput,
  value: ConfigType,
  {
    isPickup = false,
    orderLabel = 'this POS order',
  }: Pick<AvailabilityOptions, 'isPickup'> & { orderLabel?: string } = {}
): AvailabilityWarning | null {
  const reason = getPosSoftOverrideDateReason(availability, value)

  if (reason === STORE_CLOSED_REASON) {
    return getFulfillmentDateAvailabilityWarning(availability, value, {
      isPickup,
      description: `This date is marked closed by an availability change. You can still use it for ${orderLabel} if you can fulfill it.`,
      title: 'Store closed on this date',
    })
  }

  const label = isPickup ? 'pickup' : 'delivery'

  return getFulfillmentDateAvailabilityWarning(availability, value, {
    isPickup,
    description: `This ${label} date is outside the location's normal ${label} availability. You can still use it for ${orderLabel} if you can fulfill it.`,
  })
}

export function resolvePosFulfillmentLocation(
  cart:
    | {
        location_id?: LocationId | null
        location?: LocationInput
        locations?: PosFulfillmentLocation[] | null
      }
    | null
    | undefined,
  {
    selectedLocation = null,
    locations = [],
    defaultLocation = null,
  }: {
    selectedLocation?: LocationInput
    locations?: PosFulfillmentLocation[] | null
    defaultLocation?: LocationInput
  } = {}
): LocationInput {
  const selectedLocationId = Number(
    selectedLocation?.id ?? cart?.location_id ?? cart?.location?.id ?? defaultLocation?.id ?? 0
  )

  const hydratedCartLocation = (cart?.locations || []).find(
    (location) => Number(location?.id ?? 0) === selectedLocationId
  )
  const selectedListLocation = (locations || []).find(
    (location) => Number(location?.id ?? 0) === selectedLocationId
  )

  if (hydratedCartLocation) {
    return hydratedCartLocation
  }

  if (Number(selectedLocation?.id ?? 0) === selectedLocationId) {
    return selectedLocation
  }

  if (selectedListLocation) {
    return selectedListLocation
  }

  if (Number(cart?.location?.id ?? 0) === selectedLocationId) {
    return cart?.location
  }

  if (Number(defaultLocation?.id ?? 0) === selectedLocationId) {
    return defaultLocation
  }

  return selectedLocation ?? cart?.location ?? selectedListLocation ?? defaultLocation ?? null
}
